// AuthContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getCookie, setCookie, removeCookie } from "../utils/cookieHelper";
import { UserCookie } from "../utils/constants";
import {
  checkEmptyVal,
  checkObjNullorEmpty,
  GetCookieValues,
} from "../utils/common";
import { clearPendingDeepLinkUrl } from "../utils/deepLinkHelper";
import {
  getPortalClientIcon,
  getPortalClientId,
  getPortalClientLogo,
  getPortalClientName,
  getPortalClientUrl,
  isClientSessionValid,
  refreshPortalClientCookie,
} from "../helpers/clientHelper";
import {
  broadcast,
  BroadcastEvents,
  onBroadcast,
} from "../helpers/broadcastHelper";
import ClientConnectLoader from "../components/common/ClientConnectLoader";

// How long the AssetsWatch -> client loader shows before a client user leaves on logout.
const ClientLogoutLoaderMs = 1500;

// Create the AuthContext
const AuthContext = createContext();

// Provide the AuthContext to the application
export const AuthProvider = ({ children }) => {
  const [loggedinUser, setloggedInUser] = useState(null);
  // _u as last seen by this tab; null right after this tab logs out, so its own logout isn't
  // mistaken for a logout from another tab.
  const lastUserCookieRef = useRef(null);
  // { name, logo } while the logout loader for a client user is showing.
  const [clientLogoutLoader, setClientLogoutLoader] = useState(null);

  // Check for auth token in cookies when the component mounts
  useEffect(() => {
    setloggedInUser(getCookie(UserCookie.CookieName));
  }, []);

  const isAuthenticated = () => {
    let loggedinUser = getCookie(UserCookie.CookieName);
    if (
      checkEmptyVal(loggedinUser) ||
      checkObjNullorEmpty(loggedinUser) ||
      loggedinUser?.accid == 0
    ) {
      return false;
    } else {
      return true;
    }
  };

  // Login function to authenticate the user and set the auth token
  const loginUser = (loginData, isRemember, sessionid) => {
    let defaultProfile = loginData?.Profiles?.find(
      (p) => p.ProfileId == loginData.DefaultProfileId
    ) ||
      loginData?.Profiles?.[0] || {
        ProfileId: 0,
        Name: "",
        PicPath: "",
        ProfileTypeId: 0,
        ProfileType: "",
        CountryId: 0,
        ProfileCategoryIds: "",
      };
    let cookieVal = {
      [UserCookie.AccountId]: loginData.AccountId,
      [UserCookie.SessionId]: sessionid,
      [UserCookie.Email]: loginData.Email,
      [UserCookie.PlanId]: loginData.PlanId,
      [UserCookie.Plan]: loginData.Plan,
      [UserCookie.IsSetupCompleted]: loginData.IsSetupCompleted,
      [UserCookie.ClientId]: getPortalClientId(),
      [UserCookie.ClientLogo]: getPortalClientLogo(),
      [UserCookie.ClientPortalUrl]: getPortalClientUrl(),
      [UserCookie.Profile]: {
        [UserCookie.ProfileId]: defaultProfile.ProfileId,
        [UserCookie.Name]: defaultProfile.Name,
        [UserCookie.ProfilePic]: loginData.PicPath,
        [UserCookie.ProfileTypeId]: defaultProfile.ProfileTypeId,
        [UserCookie.ProfileType]: defaultProfile.ProfileType,
        [UserCookie.AccountTypeId]: loginData.AccountTypeId,
        [UserCookie.AccountType]: loginData.AccountType,
        [UserCookie.CountryId]: defaultProfile.CountryId,
        [UserCookie.ProfileCategoryIds]: defaultProfile.ProfileCategoryIds,
      },
    };

    const cookieOptions = isRemember == true && { expires: 30 };
    setCookie(UserCookie.CookieName, JSON.stringify(cookieVal), cookieOptions);
    refreshPortalClientCookie(cookieOptions);
    setloggedInUser(getCookie(UserCookie.CookieName));
    broadcast(BroadcastEvents.Login);
  };

  const updateUserProfile = (profileData, directCookieVal = null) => {
    let cookieVal = {};
    if (checkObjNullorEmpty(directCookieVal))
      cookieVal = {
        [UserCookie.AccountId]: loggedinUser?.[UserCookie.AccountId],
        [UserCookie.SessionId]: loggedinUser?.[UserCookie.SessionId],
        [UserCookie.Email]: loggedinUser?.[UserCookie.Email],
        [UserCookie.PlanId]: loggedinUser?.[UserCookie.PlanId],
        [UserCookie.Plan]: loggedinUser?.[UserCookie.Plan],
        [UserCookie.IsSetupCompleted]:
          loggedinUser?.[UserCookie.IsSetupCompleted],
        [UserCookie.ClientId]: loggedinUser?.[UserCookie.ClientId] ?? 0,
        [UserCookie.ClientLogo]: loggedinUser?.[UserCookie.ClientLogo] ?? "",
        [UserCookie.ClientPortalUrl]: loggedinUser?.[UserCookie.ClientPortalUrl] ?? "",
        [UserCookie.Profile]: {
          [UserCookie.ProfileId]: profileData.profileid,
          [UserCookie.Name]: profileData.profilename,
          [UserCookie.ProfilePic]: loggedinUser?.p?.[UserCookie.ProfilePic],
          [UserCookie.ProfileTypeId]: profileData.profiletypeid,
          [UserCookie.ProfileType]: profileData.profiletype,
          [UserCookie.AccountTypeId]:
            loggedinUser?.p?.[UserCookie.AccountTypeId],
          [UserCookie.AccountType]: loggedinUser?.p?.[UserCookie.AccountType],
          [UserCookie.CountryId]: profileData.countryid,
          [UserCookie.ProfileCategoryIds]: profileData.profilecatids,
        },
      };
    else {
      cookieVal = { ...directCookieVal };
    }

    //logoutUser();
    setCookie(UserCookie.CookieName, JSON.stringify(cookieVal), {
      expires: 30,
    });
    refreshPortalClientCookie({ expires: 30 });
    setloggedInUser(getCookie(UserCookie.CookieName));
    broadcast(BroadcastEvents.UserUpdated);
  };

  const updateUserPlan = (planData) => {
    let cookieVal = {
      ...loggedinUser,
      [UserCookie.PlanId]: planData.PlanId,
      [UserCookie.Plan]: planData.Plan,
    };

    //logoutUser();
    setCookie(UserCookie.CookieName, JSON.stringify(cookieVal), {
      expires: 30,
    });
    refreshPortalClientCookie({ expires: 30 });
    setloggedInUser(getCookie(UserCookie.CookieName));
    broadcast(BroadcastEvents.UserUpdated);
  };

  // Client portal URL a logged-out client user returns to ("" for direct AssetsWatch users).
  const getLogoutPortalUrl = (userCookie) =>
    getPortalClientId() > 0 ? getPortalClientUrl(userCookie) : "";

  // Logout function to remove the auth token.
  // redirectUrl: leave the page right after logout. React state is not updated first, because a
  //   re-render (ProtectedRoute's <Navigate>) can cancel the redirect.
  // toClientPortal: false sends other tabs to the AssetsWatch home instead of the client site.
  // showClientLoader: with redirectUrl, show the AssetsWatch -> client loader for
  //   ClientLogoutLoaderMs before leaving. The user is already logged out while it shows.
  const logoutUser = ({
    redirectUrl = "",
    toClientPortal = true,
    showClientLoader = false,
  } = {}) => {
    // Some pages call logout when nobody is signed in; only tell other tabs about a real logout.
    const userCookie = getCookie(UserCookie.CookieName);
    const wasLoggedIn = !checkObjNullorEmpty(userCookie);
    // Read before the cookies are removed: other tabs can't, and need it to return a client user to their site.
    const clientPortalUrl = toClientPortal ? getLogoutPortalUrl(userCookie) : "";
    const clientLoader =
      showClientLoader && redirectUrl
        ? {
            name: getPortalClientName(),
            logo: getPortalClientIcon() || getPortalClientLogo(userCookie),
          }
        : null;
    clearPendingDeepLinkUrl();
    removeCookie(UserCookie.CookieName, { path: "/" });
    removeCookie("_cli", { path: "/" });
    lastUserCookieRef.current = null;
    if (wasLoggedIn) broadcast(BroadcastEvents.Logout, { clientPortalUrl });
    if (redirectUrl) {
      if (clientLoader) {
        // The overlay covers whatever re-renders underneath; leave only after it has shown.
        setClientLogoutLoader(clientLoader);
        setTimeout(
          () => window.location.replace(redirectUrl),
          ClientLogoutLoaderMs
        );
      } else {
        window.location.replace(redirectUrl);
      }
      return;
    }
    setloggedInUser(null);
  };

  // Keep other open tabs in sync with login / logout / profile or plan changes made in this tab.
  useEffect(
    () =>
      onBroadcast(
        [BroadcastEvents.Logout, BroadcastEvents.Login, BroadcastEvents.UserUpdated],
        ({ type, data }) => {
          if (type === BroadcastEvents.Logout) {
            // Leaving the page; no state update so nothing re-renders and cancels the redirect.
            lastUserCookieRef.current = null;
            window.location.replace(data?.clientPortalUrl || "/");
          } else if (type === BroadcastEvents.Login) {
            window.location.reload();
          } else {
            setloggedInUser(getCookie(UserCookie.CookieName));
          }
        }
      ),
    []
  );

  // A user who signed in through a client (_u clid > 0) must keep a matching _cli
  // cookie. If _cli is deleted or changed, log out and go to the AssetsWatch home.
  // Also catches a logout from another tab whose broadcast this tab missed (tab asleep or
  // no BroadcastChannel): _u is gone, so return a client user to their site, others to "/".
  // Cookie deletion fires no event, so check on an interval and when the tab regains focus.
  useEffect(() => {
    const checkClientSession = () => {
      const userCookie = getCookie(UserCookie.CookieName);
      const lastUserCookie = lastUserCookieRef.current;
      lastUserCookieRef.current = checkObjNullorEmpty(userCookie) ? null : userCookie;
      if (!checkObjNullorEmpty(lastUserCookie) && checkObjNullorEmpty(userCookie)) {
        const clid = parseInt(lastUserCookie?.[UserCookie.ClientId], 10) || 0;
        window.location.replace(
          (clid > 0 && lastUserCookie?.[UserCookie.ClientPortalUrl]) || "/"
        );
        return;
      }
      if (isClientSessionValid()) return;
      logoutUser({ redirectUrl: "/", toClientPortal: false });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkClientSession();
    };

    checkClientSession();
    const timer = setInterval(checkClientSession, 3000);
    window.addEventListener("focus", checkClientSession);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", checkClientSession);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        loginUser,
        logoutUser,
        loggedinUser,
        isAuthenticated,
        updateUserProfile,
        updateUserPlan,
      }}
    >
      {children}
      {clientLogoutLoader && (
        <ClientConnectLoader
          overlay
          reverse
          clientName={clientLogoutLoader.name}
          clientLogo={clientLogoutLoader.logo}
          title={
            clientLogoutLoader.name
              ? `Returning to ${clientLogoutLoader.name}`
              : "Signing you out"
          }
          copy="Signing you out securely."
        />
      )}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);
